// ============================================================
// InstanceChips - the talents or traits on a sheet, as chips.
//
// A chip is the entry's name, its parameter (typed in place), a tag when
// the engine does not apply its rules, and a remove. A custom entry is
// drawn dashed; a talent granted by a trait is drawn dimmed, with no
// remove, because it is not on the sheet to take off.
// ============================================================
import type { Character } from '../../engine/character/types';
import clsx from '../../utils/clsx';
import { KINDS, entryFor, type Instance, type InstanceKind } from './instanceKinds';
import styles from './Sheet.module.css';

interface Props {
  kind: InstanceKind;
  character: Character;
  onChange: (partial: Partial<Character>) => void;
  disabled?: boolean;
}

interface ChipProps extends Props {
  instance: Instance;
  index: number;
}

function Chip({ kind, character, onChange, disabled, instance, index }: ChipProps) {
  const api = KINDS[kind];
  const text = api.textOf(instance);
  const entry = entryFor(kind, instance.id);
  const name = text?.name ?? instance.id;
  const slot = `${instance.id}-${index}`;
  const tag = entry?.coverageTag;
  return (
    <span
      className={clsx(styles.chip, styles[`chip_${kind}`], text?.custom && styles.chipCustom)}
      data-testid={`${kind}-chip-${slot}`}
      title={text?.description || undefined}
    >
      {entry?.parameterized ? (
        <label className={styles.chipLabel}>
          {name}
          <input
            type="text"
            className={styles.chipParam}
            data-testid={`${kind}-param-${slot}`}
            aria-label={`${name} ${entry.parameterLabel ?? 'parameter'}`}
            placeholder={entry.parameterLabel ?? 'value'}
            value={instance.parameter ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(api.setAt(character, index, { parameter: e.target.value }))}
          />
        </label>
      ) : (
        <span>{name}</span>
      )}
      {text?.custom && <i className={styles.chipTag}>custom</i>}
      {tag && <i className={styles.chipTag}>{tag}</i>}
      <button
        type="button"
        className={styles.chipRemove}
        aria-label={`Remove ${name}`}
        disabled={disabled}
        onClick={() => onChange(api.removeAt(character, index))}
      >
        ×
      </button>
    </span>
  );
}

export default function InstanceChips({ kind, character, onChange, disabled }: Props) {
  const api = KINDS[kind];
  const instances = api.instancesOf(character);
  // What something else on the sheet confers (a trait's talents): dimmed, never removable.
  const granted = api.grantedOf(character);
  if (instances.length === 0 && granted.length === 0) {
    return <div className={styles.sectionNote}>None. Add one with + ADD.</div>;
  }
  return (
    <>
      <div className={styles.chips}>
        {instances.map((instance, index) => (
          // By index: one catalog entry may be taken more than once.
          <Chip key={`${index}-${instance.id}`} {...{ kind, character, onChange, disabled, instance, index }} />
        ))}
      </div>
      {granted.length > 0 && (
        <div className={styles.chips} data-testid={`${kind}-granted`}>
          <span className={styles.readoutLabel}>Granted by traits</span>
          {granted.map((instance, i) => (
            <span key={`${i}-${instance.id}`} className={clsx(styles.chip, styles.chipGranted)}>
              {api.textOf(instance)?.name ?? instance.id}
              {instance.parameter ? ` (${instance.parameter})` : ''}
            </span>
          ))}
        </div>
      )}
    </>
  );
}
